/**
 * Team Member Profile — /team/{slug}
 * ---------------------------------------------------------------------------
 * Split editorial layout for company team members (leadership, executives)
 * shown on /company.
 *   Left:  identity stack (portrait, name, position, divider, bio).
 *   Right: small B&W roster of the rest of the team — quiet side-rail nav
 *          for hopping between profiles without going back to /company.
 * On mobile/tablet the columns stack (identity first, then roster).
 */

import MetaHead from '@/components/meta-head';
import PublicLayout from '@/layouts/public-layout';
import { Link } from '@inertiajs/react';
import DOMPurify from 'isomorphic-dompurify';

interface TeamMember {
    id: number;
    name: string;
    slug: string;
    position: string | null;
    department: string | null;
    main_image: string | null;
    bio: string | null;
}

interface RosterMember {
    id: number;
    name: string;
    slug: string;
    position: string | null;
    main_image: string | null;
}

interface EliteAgent {
    id: number;
    first_name: string;
    last_name: string;
    slug: string;
    main_image: string | null;
}

interface TeamShowProps {
    member: TeamMember;
    otherMembers: RosterMember[];
    eliteAgents: EliteAgent[];
}

function getInitials(name: string): string {
    const parts = name.trim().split(/\s+/);
    const first = parts[0]?.[0] ?? '';
    const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : '';
    return (first + last).toUpperCase();
}

export default function TeamShow({ member, otherMembers, eliteAgents }: TeamShowProps) {
    const plainBio = member.bio?.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim() ?? '';
    const description = plainBio
        ? plainBio.length > 160
            ? plainBio.slice(0, 157).trimEnd() + '…'
            : plainBio
        : `${member.name}${member.position ? ' — ' + member.position : ''} at LuxRally Travel.`;

    return (
        <PublicLayout>
            <MetaHead title={`${member.name}${member.position ? ' · ' + member.position : ''} | LuxRally Travel`} description={description} />

            <article className="min-h-[80vh] bg-[#fdfaf3] pt-12 pb-24 sm:pt-16 sm:pb-28 md:pt-24 md:pb-32">
                <div className="mx-auto w-full max-w-[34rem] px-6 lg:max-w-[68rem]">
                    <div className="grid grid-cols-1 gap-16 lg:grid-cols-[1fr_18rem] lg:gap-20">
                        {/* Left — identity stack + cinema band */}
                        <div>
                            <div className="mx-auto w-full max-w-[34rem]">
                            <div className="mx-auto aspect-square w-28 overflow-hidden rounded-full bg-[#0e2727]/5 sm:w-32">
                                {member.main_image ? (
                                    <img
                                        src={member.main_image}
                                        alt={member.name}
                                        className="h-full w-full object-cover"
                                        loading="eager"
                                        decoding="async"
                                    />
                                ) : (
                                    <div className="flex h-full w-full items-center justify-center">
                                        <span className="lux-didot lux-didot-lg text-[#0e2727]/25">{getInitials(member.name)}</span>
                                    </div>
                                )}
                            </div>

                            <header className="mt-8 text-center sm:mt-10">
                                <h1 className="lux-didot lux-didot-lg text-[#0b2222]">{member.name}</h1>
                                {member.position && <p className="lux-caps lux-caps-sm mt-3 text-[#0b2222]/55">{member.position}</p>}
                                <p className="mt-3 inline-flex items-center gap-1.5 text-[#0b2222]/45">
                                    <svg viewBox="0 0 7 12" fill="currentColor" aria-hidden="true" className="-mt-0.5 h-3 w-auto">
                                        <path d="M3.5 0a3.5 3.5 0 0 0-3.5 3.5c0 4.25 3.5 8.5 3.5 8.5s3.5-4.25 3.5-8.5a3.5 3.5 0 0 0-3.5-3.5z" />
                                    </svg>
                                    <span className="lux-caps lux-caps-sm">Miami, FL</span>
                                </p>
                            </header>

                            {member.bio && (
                                <div
                                    className="lux-body lux-body-sm mt-8 text-center text-[#0e2727]/85 [&_p+p]:mt-4 sm:mt-10"
                                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(member.bio) }}
                                />
                            )}

                            {/* What's Next — editorial close-out, inline magazine idiom */}
                            <div className="mt-14 sm:mt-16">
                                <div className="mx-auto h-px w-12 bg-[#0e2727]/20" aria-hidden="true" />
                                <div className="mt-8 flex flex-wrap items-center justify-center gap-x-4 gap-y-2 text-center">
                                    <span className="lux-caps lux-caps-sm text-[#0b2222]/45">What's Next?</span>
                                    <Link
                                        href="/clients"
                                        className="lux-caps lux-caps-sm text-[#0b2222] underline decoration-[#0b2222]/0 underline-offset-4 transition-all duration-300 hover:decoration-[#0b2222]"
                                    >
                                        Become a Client
                                    </Link>
                                    <span className="lux-caps lux-caps-sm text-[#0b2222]/35">or</span>
                                    <Link
                                        href="/agents/become-an-agent/apply"
                                        className="lux-caps lux-caps-sm text-[#0b2222] underline decoration-[#0b2222]/0 underline-offset-4 transition-all duration-300 hover:decoration-[#0b2222]"
                                    >
                                        Become an Agent
                                    </Link>
                                </div>
                            </div>
                            </div>

                            {/* Cinema band — invites visitors into /company; reuses the company-page hero video */}
                            <Link
                                href="/company"
                                className="group mt-20 block sm:mt-24"
                                aria-label="Explore our story — visit /company"
                            >
                                <div className="relative aspect-[16/9] w-full overflow-hidden rounded-sm lg:aspect-[21/9]">
                                    <video
                                        autoPlay
                                        muted
                                        loop
                                        playsInline
                                        preload="metadata"
                                        poster="https://cdnweb.luxrallytravel.com/images/rally-video-poster.jpg"
                                        className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.03]"
                                    >
                                        <source
                                            src="https://cdnweb.luxrallytravel.com/parity-migration/2025/08/8f0f88f8-3f32-4a90-8908-a5c595bd42fb.mp4"
                                            type="video/mp4"
                                        />
                                    </video>
                                </div>
                                <p className="lux-caps lux-caps-sm mt-4 flex items-center justify-end gap-2 text-[#0b2222]/75 transition-all duration-300 group-hover:gap-3 group-hover:text-[#0b2222]">
                                    Explore the Journey
                                    <span aria-hidden="true">→</span>
                                </p>
                            </Link>
                        </div>

                        {/* Right — team roster side rail */}
                        {otherMembers.length > 0 && (
                            <aside aria-label="More from the team">
                                <p className="lux-caps lux-caps-sm text-[#0b2222]/45">The Team</p>
                                <ul className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 sm:gap-x-6 lg:grid-cols-1 lg:gap-x-0">
                                    {otherMembers.map((m) => (
                                        <li key={m.id}>
                                            <Link
                                                href={`/team/${m.slug}`}
                                                className="group flex items-center gap-4 transition-opacity duration-300 hover:opacity-80"
                                            >
                                                <div className="aspect-square w-12 shrink-0 overflow-hidden rounded-full bg-[#0e2727]/5">
                                                    {m.main_image ? (
                                                        <img
                                                            src={m.main_image}
                                                            alt=""
                                                            loading="lazy"
                                                            decoding="async"
                                                            className="h-full w-full object-cover grayscale"
                                                        />
                                                    ) : (
                                                        <div className="flex h-full w-full items-center justify-center">
                                                            <span className="text-[10px] font-medium tracking-[0.1em] text-[#0e2727]/35">
                                                                {getInitials(m.name)}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="min-w-0 flex-1">
                                                    <p className="lux-didot text-[15px] leading-tight text-[#0b2222] transition-colors duration-300 group-hover:text-[#0e2727]">
                                                        {m.name}
                                                    </p>
                                                    {m.position && <p className="lux-caps lux-caps-sm mt-1 text-[#0b2222]/50">{m.position}</p>}
                                                </div>
                                            </Link>
                                        </li>
                                    ))}
                                </ul>

                                {/* Meet our advisors — collage of top elite-circle agents, links to /the-team */}
                                {eliteAgents.length > 0 && (
                                    <Link
                                        href="/the-team"
                                        className="group mt-12 block"
                                        aria-label="Meet our advisors — visit /the-team"
                                    >
                                        <svg
                                            viewBox="0 0 507 28"
                                            fill="currentColor"
                                            aria-hidden="true"
                                            overflow="visible"
                                            className="h-3 w-auto text-[#0b2222]/55 transition-colors duration-300 group-hover:text-[#0b2222] sm:h-3.5"
                                        >
                                            <circle cx="13" cy="13" r="10" fill="none" stroke="currentColor" strokeWidth="5" />
                                            <line x1="21" y1="21" x2="29" y2="29" stroke="currentColor" strokeWidth="5" />
                                            <text
                                                x="40"
                                                y="27"
                                                fontFamily="ui-sans-serif, system-ui, sans-serif"
                                                fontWeight="800"
                                                fontSize="38"
                                                letterSpacing="0.12em"
                                                fontStyle="italic"
                                            >
                                                SEARCH ADVISORS
                                            </text>
                                        </svg>

                                        <div className="mt-5 grid w-full grid-cols-5 gap-1.5 sm:w-fit">
                                            {eliteAgents.map((a, idx) => (
                                                <div
                                                    key={a.id}
                                                    className={`aspect-square overflow-hidden rounded-sm bg-[#0e2727]/5 sm:w-24 lg:w-10 ${idx >= 10 ? 'lg:hidden' : ''}`}
                                                    title={`${a.first_name} ${a.last_name}`}
                                                >
                                                    {a.main_image ? (
                                                        <img
                                                            src={a.main_image}
                                                            alt=""
                                                            loading="lazy"
                                                            decoding="async"
                                                            className="h-full w-full object-cover grayscale transition-all duration-500 group-hover:grayscale-0"
                                                        />
                                                    ) : (
                                                        <div className="flex h-full w-full items-center justify-center">
                                                            <span className="text-[8px] font-medium tracking-[0.1em] text-[#0e2727]/35">
                                                                {a.first_name?.[0]}
                                                                {a.last_name?.[0]}
                                                            </span>
                                                        </div>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </Link>
                                )}
                            </aside>
                        )}
                    </div>
                </div>
            </article>
        </PublicLayout>
    );
}
